import {useEffect, useState, type FC} from 'react'
import {Form, Spinner} from 'react-bootstrap'
import {toast} from 'react-toastify'
import * as Yup from 'yup'

import {
  useCreateKnowledgeMutation,
  useUpdateKnowledgeMutation,
} from '../../../../services/knowledgeServiceApi'
import {catchHandler} from '../../../../utils/http/catchHandler'
import {BsPlus} from 'react-icons/bs'
import {getWorkflows} from '../../../data/global/workflow'
import {validateField} from './data/knowledgeSchema'
import {useTranslation} from 'react-i18next'
import ISO6391 from 'iso-639-1'
import {useAuth} from '../../../../app/modules/auth'

interface SubKnowledgeItem {
  workflow: string
  voice: string
  do_s: string
  don_t: string
  content_example?: string
  sub_category?: string
}

interface KnowledgeFormProps {
  refetch: () => void
  data?: {
    public_id: string
    instructions: string
    sub_knowledge?: SubKnowledgeItem[]
  } | null
}

// Note:
// Assistant have 32k characters limit.
// We have total 700 characters limit per workflows instructions

const KnowledgeForm: FC<KnowledgeFormProps> = ({refetch, data}) => {
  const [createKnowledge, {isLoading: isCreating}] = useCreateKnowledgeMutation()
  const [updateKnowledge, {isLoading: isUpdating}] = useUpdateKnowledgeMutation()
  const {currentUser} = useAuth()

  const [formState, setFormState] = useState({
    instructions: '',
    public_id: null as string | null,
    sub_knowledge: [] as SubKnowledgeItem[],
  })

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
  const [subKnowledgeErrors, setSubKnowledgeErrors] = useState<
    Record<string, Record<string, string>>
  >({})

  const {t} = useTranslation()

  const isEditing = !!data?.public_id
  const isLoading = isCreating || isUpdating

  useEffect(() => {
    if (data) {
      setFormState({
        instructions: data.instructions || '',
        public_id: data.public_id,
        sub_knowledge: data.sub_knowledge || [],
      })
    } else {
      addSubKnowledge()
    }
  }, [data])

  // Handle input changes
  const handleInputChange = async (field: string, value: any) => {
    setFormState((prev) => ({...prev, [field]: value}))
    await validateField(field, value, setValidationErrors)
  }

  const customSubCategoryData = {
    '9c6d8c5d-6d7d-4e3a-9e1d-5b3f1c2a0b4f': ['Facebook', 'Instagram', 'X', 'Linkedin'],
    '6ba7b810-9dad-11d1-80b4-00c04fd430c9': ['Google', 'Meta', 'Linkedin'],
  }

  const customWorkflowIds = [
    '9c6d8c5d-6d7d-4e3a-9e1d-5b3f1c2a0b4f',
    '6ba7b810-9dad-11d1-80b4-00c04fd430c9',
  ]

  const handleSubKnowledgeChange = (index: number, field: string, value: string) => {
    if (field === 'workflow') {
      const isCustom = customWorkflowIds.includes(value)

      const selectedWorkflows = formState.sub_knowledge
        .filter((_, i) => i !== index)
        .map((item) => item.workflow)

      if (!isCustom) {
        const alreadySelected = selectedWorkflows.some(
          (wf) => wf === value && !customWorkflowIds.includes(wf)
        )
        if (alreadySelected) {
          toast.error('This workflow is already selected')
          return
        }
      } else {
        const count = selectedWorkflows.filter((wf) => wf === value).length
        if (count >= 3) {
          toast.error("You can't select more than 3 sub categories")
          return
        }
      }
    }

    setFormState((prev) => {
      const updatedSubKnowledge = [...prev.sub_knowledge]
      updatedSubKnowledge[index] = {
        ...updatedSubKnowledge[index],
        [field]: value,
      }
      return {
        ...prev,
        sub_knowledge: updatedSubKnowledge,
      }
    })

    // Validate sub-knowledge field
    if (!value) {
      setSubKnowledgeErrors((prev) => ({
        ...prev,
        [index]: {
          ...prev[index],
          // [field]: `${
          //   field === 'workflow'
          //     ? 'Workflow'
          //     : field === 'voice'
          //     ? 'Brand voice'
          //     : field === 'do_s'
          //     ? 'Dos field'
          //     : field === 'don_t'
          //     ? "Don't field"
          //     : 'Content example'
          // } is required`,
        },
      }))
    } else {
      setSubKnowledgeErrors((prev) => ({
        ...prev,
        [index]: {
          ...prev[index],
          [field]: '',
        },
      }))
    }
  }

  // Add new sub-knowledge item
  const addSubKnowledge = () => {
    setFormState((prev) => ({
      ...prev,
      sub_knowledge: [
        ...prev.sub_knowledge,
        {workflow: '', voice: '', do_s: '', don_t: '', content_example: ''},
      ],
    }))
  }

  // Remove sub-knowledge item
  const removeSubKnowledge = (index: number) => {
    setFormState((prev) => {
      const updatedSubKnowledge = [...prev.sub_knowledge]
      updatedSubKnowledge.splice(index, 1)
      return {
        ...prev,
        sub_knowledge: updatedSubKnowledge,
      }
    })

    // Remove errors for this index
    setSubKnowledgeErrors((prev) => {
      const updated = {...prev}
      delete updated[index]
      return updated
    })
  }

  const handleResetForm = () => {
    setFormState({
      instructions: '',
      public_id: null,
      sub_knowledge: [],
    })
    setValidationErrors({})
    setSubKnowledgeErrors({})
  }

  // Form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    let hasErrors = false
    formState.sub_knowledge.forEach((item, index) => {
      if (!item.workflow) {
        setSubKnowledgeErrors((prev) => ({
          ...prev,
          [index]: {...prev[index], workflow: 'Workflow is required'},
        }))
        hasErrors = true
      }
      // if (!item.content_example) {
      //   setSubKnowledgeErrors((prev) => ({
      //     ...prev,
      //     [index]: {...prev[index], content_example: 'Content example is required'},
      //   }))
      //   hasErrors = true
      // }
    })

    try {
      await validateField('instructions', formState.instructions, setValidationErrors)

      if (validationErrors.name || validationErrors.instructions || hasErrors) {
        toast.error('Please fix all errors in the form')
        return
      }

      const data = {
        id: formState.public_id,
        instructions: formState.instructions,
        sub_knowledge: formState.sub_knowledge,
      }

      if (isEditing) {
        updateKnowledge({data, public_id: formState.public_id})
          .unwrap()
          .then(() => {
            refetch()
            toast.success(`Knowledge updated successfully`)
          })
          .catch(catchHandler)
      } else {
        createKnowledge(data)
          .unwrap()
          .then((response) => {
            refetch()
            toast.success(`Knowledge created successfully`)
          })
          .catch(catchHandler)
          .finally(() => handleResetForm())
      }
    } catch (validationError) {
      if (validationError instanceof Yup.ValidationError) {
        const errors: Record<string, string> = {}
        validationError.inner.forEach((err) => {
          if (err.path) {
            errors[err.path] = err.message
          }
        })

        setValidationErrors(errors)

        if (validationError.inner.length > 0 && validationError.inner[0].message) {
          toast.error(validationError.inner[0].message)
        }
      }
    }
  }

  return (
    <div className='border p-4' style={{borderRadius: '24px'}}>
      <h2 className='mb-3'>{t('knowledge.title')}</h2>
      <h5 className='mb-4'>{t('knowledge.subtitle')}</h5>
      <form onSubmit={handleSubmit}>
        <div className='mb-4'>
          <label htmlFor='knowledgeText' className='form-label mb-2 fs-6'>
            {t('knowledge.inputs.text.label')}
          </label>
          <Form.Control
            id='knowledgeText'
            name='instructions'
            as='textarea'
            className={`form-control ${validationErrors.instructions ? 'is-invalid' : ''}`}
            placeholder={t('knowledge.inputs.text.placeholder')}
            style={{
              height: '150px',
              resize: 'vertical',
              borderRadius: '8px',
            }}
            value={formState.instructions}
            onChange={(e) => handleInputChange('instructions', e.target.value)}
            onBlur={() =>
              validateField('instructions', formState.instructions, setValidationErrors)
            }
          />
          {validationErrors.instructions && (
            <div className='invalid-feedback'>{validationErrors.instructions}</div>
          )}
          <div className='text-muted mt-2 small d-flex justify-content-between'>
            <span>
              {' '}
              {t('knowledge.min')}: 3 {t('knowledge.characters')}
            </span>
            <span>
              {' '}
              {t('knowledge.max')}: 500 {t('knowledge.characters')}
            </span>
          </div>
        </div>

        <div className='border p-3 mb-4'>
          <div className='d-flex justify-content-between align-items-center mb-3'>
            <div>
              <h5 className='mb-0'> {t('knowledge.inputs.text.label')}</h5>
              <p className='text-muted'>{t('knowledge.inputs.text.placeholder')}</p>
            </div>
            <button
              type='button'
              className='btn btn-outline-primary d-flex align-items-center justify-content-center'
              onClick={addSubKnowledge}
              style={{borderRadius: '8px', padding: '3px'}}
            >
              <BsPlus size={24} />
            </button>
          </div>
          <div className='mb-4' style={{maxHeight: '300px', overflowY: 'auto'}}>
            {formState.sub_knowledge.map((item, index) => (
              <div key={index} className='mb-3 border rounded p-3 pb-4 position-relative'>
                <button
                  type='button'
                  className='btn btn-sm position-absolute end-0 top-0 mt-1 me-1'
                  onClick={() => removeSubKnowledge(index)}
                  style={{fontSize: '1.2rem', lineHeight: 1}}
                >
                  &times;
                </button>

                <div className='row g-3'>
                  <div className='col-md-6'>
                    <label className='form-label mb-2 fs-6'> {t('knowledge.workflow')}</label>
                    <Form.Select
                      className={subKnowledgeErrors[index]?.workflow ? 'is-invalid' : ''}
                      value={item.workflow}
                      onChange={(e) => handleSubKnowledgeChange(index, 'workflow', e.target.value)}
                      style={{borderRadius: '8px'}}
                    >
                      <option value=''> {t('knowledge.select_workflow')}</option>
                      {getWorkflows(ISO6391.getCode(currentUser?.language || 'en')).map(
                        (workflow) => (
                          <option key={workflow.uuid} value={workflow.uuid}>
                            {workflow.name}
                          </option>
                        )
                      )}
                    </Form.Select>
                    {subKnowledgeErrors[index]?.workflow && (
                      <div className='invalid-feedback'>{subKnowledgeErrors[index].workflow}</div>
                    )}

                    {/* "Social Media & Digital Ad" */}
                    {(item.workflow === '9c6d8c5d-6d7d-4e3a-9e1d-5b3f1c2a0b4f' ||
                      item.workflow === '6ba7b810-9dad-11d1-80b4-00c04fd430c9') && (
                      <>
                        <label className='form-label mb-2 fs-6  mt-3'>
                          {t('knowledge.sub_category')}
                        </label>
                        <Form.Select
                          className={subKnowledgeErrors[index]?.workflow ? 'is-invalid' : ''}
                          value={item.sub_category}
                          onChange={(e) =>
                            handleSubKnowledgeChange(index, 'sub_category', e.target.value)
                          }
                          style={{borderRadius: '8px'}}
                        >
                          <option value=''>{t('knowledge.select_sub_category')}</option>
                          {customSubCategoryData[item.workflow].map((item) => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                        </Form.Select>
                        {subKnowledgeErrors[index]?.workflow && (
                          <div className='invalid-feedback'>
                            {subKnowledgeErrors[index].workflow}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  <div className='col-md-6'>
                    <div className='col-md-12 mb-3'>
                      <label className='form-label mb-2 fs-6'>
                        {' '}
                        {t('knowledge.inputs.content_example.label')}
                      </label>
                      <Form.Control
                        as='textarea'
                        maxLength={600}
                        className={`form-control ${
                          subKnowledgeErrors[index]?.content_example ? 'is-invalid' : ''
                        }`}
                        placeholder={t('knowledge.inputs.content_example.placeholder')}
                        style={{
                          borderRadius: '8px',
                          resize: 'vertical',
                          height: '100px',
                        }}
                        value={item.content_example}
                        onChange={(e) =>
                          handleSubKnowledgeChange(index, 'content_example', e.target.value)
                        }
                      />

                      <div className='text-muted mt-2 small d-flex justify-content-between'>
                        <span>
                          {t('knowledge.max')}: 600 {t('knowledge.characters')}
                        </span>
                      </div>

                      {subKnowledgeErrors[index]?.content_example && (
                        <div className='invalid-feedback'>
                          {subKnowledgeErrors[index].content_example}
                        </div>
                      )}
                    </div>

                    <div className='col-md-12 mb-3'>
                      <label className='form-label mb-2 fs-6'>{t('knowledge.voice')}</label>
                      <Form.Control
                        as='textarea'
                        maxLength={250}
                        className={`form-control ${
                          subKnowledgeErrors[index]?.voice ? 'is-invalid' : ''
                        }`}
                        placeholder={t('knowledge.inputs.voice.placeholder')}
                        style={{
                          borderRadius: '8px',
                          resize: 'vertical',
                          height: '100px',
                        }}
                        value={item.voice}
                        onChange={(e) => handleSubKnowledgeChange(index, 'voice', e.target.value)}
                      />
                      <div className='text-muted mt-2 small d-flex justify-content-between'>
                        <span>
                          {t('knowledge.max')}: 250 {t('knowledge.characters')}
                        </span>
                      </div>
                      {subKnowledgeErrors[index]?.voice && (
                        <div className='invalid-feedback'>{subKnowledgeErrors[index].voice}</div>
                      )}
                    </div>

                    <div className='col-md-12 mb-3'>
                      <label className='form-label mb-2 fs-6'>{t('knowledge.do_s')}</label>
                      <Form.Control
                        as='textarea'
                        maxLength={100}
                        className={`form-control ${
                          subKnowledgeErrors[index]?.do_s ? 'is-invalid' : ''
                        }`}
                        placeholder={t('knowledge.inputs.do_s.placeholder')}
                        style={{
                          borderRadius: '8px',
                          resize: 'vertical',
                          height: '100px',
                        }}
                        value={item.do_s}
                        onChange={(e) => handleSubKnowledgeChange(index, 'do_s', e.target.value)}
                      />
                      <div className='text-muted mt-2 small d-flex justify-content-between'>
                        <span>
                          {t('knowledge.max')}: 100 {t('knowledge.characters')}
                        </span>
                      </div>
                      {subKnowledgeErrors[index]?.do_s && (
                        <div className='invalid-feedback'>{subKnowledgeErrors[index].do_s}</div>
                      )}
                    </div>

                    <div className='col-md-12'>
                      <label className='form-label mb-2 fs-6'>{t('knowledge.don_t')}</label>
                      <Form.Control
                        as='textarea'
                        maxLength={100}
                        className={`form-control ${
                          subKnowledgeErrors[index]?.don_t ? 'is-invalid' : ''
                        }`}
                        placeholder={t('knowledge.inputs.don_t.placeholder')}
                        style={{
                          borderRadius: '8px',
                          resize: 'vertical',
                          height: '100px',
                        }}
                        value={item.don_t}
                        onChange={(e) => handleSubKnowledgeChange(index, 'don_t', e.target.value)}
                      />
                      <div className='text-muted mt-2 small d-flex justify-content-between'>
                        <span>
                          {t('knowledge.max')}: 100 {t('knowledge.characters')}
                        </span>
                      </div>

                      {subKnowledgeErrors[index]?.don_t && (
                        <div className='invalid-feedback'>{subKnowledgeErrors[index].don_t}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className='d-flex justify-content-end align-items-center mb-4'>
          <button
            type='submit'
            className='universalButton'
            style={{width: 'unset'}}
            disabled={isLoading}
          >
            {isLoading ? (
              <Spinner animation='border' role='status' size='sm'>
                <span className='visually-hidden'>Loading...</span>
              </Spinner>
            ) : (
              'Submit'
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default KnowledgeForm
